import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { netezzaSecurityLevelTypes } from 'app/model/linkedservice/netezza.linkedservice';
import { format } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';

const additionalFieldsMetadata: ControlMetadata[] = GlobalVariables.enableNetezzaPrefetch ? [{
    type: 'textbox-number',
    bind: 'prefetch',
    label: stringResources.ModelName_PreFetch,
    isLabelVertical: true,
    min: 0,
    description: stringResources.PreFetch_Description
}, {
    type: 'textbox-number',
    bind: 'socket',
    label: stringResources.ModelName_Socket,
    isLabelVertical: true,
    validators: [{
        type: 'number',
        min: 4096,
        max: 131072,
        errorMessage: stringResources.Socket_ValidationMessage
    }],
    description: stringResources.Socket_Description + stringResources.Socket_ValidationMessage
},
{
    type: 'key-val-list',
    bind: 'additionalFields',
    label: stringResources.AdditionalConnectionStringFields
}] : [{
    type: 'key-val-list',
    bind: 'additionalFields',
    label: stringResources.AdditionalConnectionStringFields
}];

const connectionForm: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'server',
        label: stringResources.ModelName_ServerName,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'port',
        label: stringResources.ModelName_Port,
        isLabelVertical: true,
        inputType: 'number',
        min: 0,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }]
    },
    {
        type: 'textbox',
        bind: 'databaseName',
        label: stringResources.ModelName_DatabaseName,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'userName',
        label: stringResources.ModelName_UserName,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    {
        type: 'dynamic-akv-switcher',
        hideAKVSwitcherInConnStrProps: true,
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            required: true,
            isLabelVertical: true,
            validators: [{ type: 'required', errorMessage: '' }]
        },
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Netezza_DisplayText),
            stringResources.ModelName_Password
        )
    },
    {
        type: 'select',
        bind: 'securityLevel',
        label: stringResources.ModelName_SecurityLevel,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.SecurityLevel_OnlyUnsecured,
                value: netezzaSecurityLevelTypes.OnlyUnSecured
            },
            {
                label: stringResources.SecurityLevel_PreferredUnSecured,
                value: netezzaSecurityLevelTypes.preferredUnSecured
            },
            {
                label: stringResources.SecurityLevel_PreferredSecured,
                value: netezzaSecurityLevelTypes.preferredSecured,
                hidden: `{ securityLevel !== '${netezzaSecurityLevelTypes.preferredSecured}' }`
            },
            {
                label: stringResources.SecurityLevel_OnlySecured,
                value: netezzaSecurityLevelTypes.onlySecured,
                hidden: `{ securityLevel !== '${netezzaSecurityLevelTypes.onlySecured}' }`
            }
        ],
        defaultValue: netezzaSecurityLevelTypes.preferredUnSecured
    },
    ...additionalFieldsMetadata
];
const selectSwitch: ControlMetadata = {
    type: 'select-button-switch',
    required: true,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.ModelName_ConnectionString,
            value: false
        },
        {
            label: stringResources.AzureKeyVault_DisplayText,
            value: true,
            hideAKVOptionIfNotChosen: false
        }
    ],
    bind: 'enableAKV',
    isNotRelatedToCredential: true,
    defaultValue: false,
    cases: [
        {
            caseValue: true,
            children: [
                {
                    type: 'azure-key-vault',
                    bind: 'referedAKV',
                    isLabelVertical: true
                }
            ]
        },
        {
            caseValue: false,
            children: connectionForm
        }
    ]
};
export const netezzaMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.netezza.linkedServiceType,
    displayName: stringResources.Netezza_DisplayText,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-netezza'
    },
    isDeprecated: GlobalVariables.showLegacy ? undefined : '{ version !== "v2" }',
    showWarningMessage: '{ version !== "v2" }',
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
        stringResources.VersionLabel.toLocaleLowerCase()
    ),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-netezza',
    isFirstClass: true,
    category: ['Database'],
    supportDatasetV2: true,
    isOnPremises: true,// Please note legacy one still supports Azure IR. We have a special handling in LS config component
    copySinkTypes: [],
    copySourceTypes: [adfModelTypeMap.netezza.copySourceType],
    SHIRVersion: {
        v2: '5.56.0.0'
    },
    supportedInTrident: true,
    supportOPDG: GlobalVariables.enableOPDG,
    opdgOnly: true,
    supportCopyJobSource: GlobalVariables.tridentBatch1ConnectorPhase2,
    supportedJobModesAsSource: GlobalVariables.copyJobIncrementalCopyPhase2 ? undefined : ['Batch'],
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'Netezza' as ResourceKind
    },
    controls: [
        {
            type: 'radio-switch',
            bind: 'version',
            label: stringResources.VersionLabel,
            isLabelVertical: true,
            required: true,
            options: driverVersionVersionOptions,
            defaultValue: driverVersions.v2,
            hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
            cases: [
                {
                    caseValue: driverVersions.v1,
                    children: [
                        {
                            type: 'hint',
                            hintMode: 'warning',
                            description: format(
                                stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                stringResources.VersionLabel.toLocaleLowerCase()),
                            link: 'https://learn.microsoft.com/azure/data-factory/connector-netezza'
                        },
                        selectSwitch
                    ]
                },
                {
                    caseValue: driverVersions.v2,
                    children: connectionForm
                }
            ]
        },
        ...(connectionForm
                .map((control) => ({
                    ...control,
                    hide: !GlobalVariables.showLegacy ? '{version !== "v2" }' : true
                }))
            )
    ]
};
